import React, { useMemo, useState } from 'react';
import { useMutation, useQuery, useReactiveVar } from '@apollo/client';
import { Button } from '@mui/material';
import ApolloError from '@shared/components/ApolloError';
import PageInstructions from '@shared/components/PageInstructions';
import StickyHeader from '@shared/components/StickyHeader';
import { Breadcrumb } from '@shared/components/breadcrumb/Breadcrumb';
import { Page } from '../../../../layout';
import { useNavigate } from 'react-router-dom';
import ErrorMessage from '@shared/components/ErrorMessage';
import ADD_EVENT from '../../../queries/AddEventMutation';
import {
  formatDate,
  getFirstAvailableDate,
  getFirstDayOfTheWeek,
  getLastDayOfTheWeek,
  getToday
} from '@shared/services/dateService';
import GET_ORGS_WITH_ACTIVE_UNITS from '../../../queries/getOrgsWithActiveUnitsQuery';
import EventInputValidator from '../../../validators/EventInputValidator';
import CalendarEventMapper from '../../../mappers/CalendarEventMapper';
import EventDates from '../inputs/EventDates';
import OrgOrgUnitSelection from '../inputs/OrgOrgUnitSelection';
import AtlSelection from '../inputs/AtlSelection';
import useAvailableAtlList from '../../../hooks/useAvailableAtlList';
import { useSelectedDateStore } from '../../../CalendarStore';
import LinkButton from '@shared/components/LinkButton';
import { userInfo } from '@apollo-internal/cache';
import { ATL_SELECTION_TYPE } from '../../shared/constants';
import { useGetUnavailablePeriodsQuery } from '../../shared/hooks/useGetUnavailablePeriodsQuery';
import PrivilegeNames from '../../../../../constants/privilegeNames';
import { ISACA_MDDAP_APPRAISER } from '../../../../../constants/system-roles';
import { APPRAISER_EVENT_TYPE } from '../../../constants/AppraiserEventType';
import { showSuccessToast, showWarningToast } from '@shared/services/toastService';
import HAS_USER_REACHED_EVENTS_LIMIT from '../../../queries/hasUserReachedEventsLimit';
import { VALIDATION_ERROR_CODES } from '@apollo-internal/constants/ValidationErrorCodes';
import { useBackgroundQuery } from '@apollo-internal/hooks/useBackgroundQuery';
import { useShallow } from 'zustand/shallow';

const AddEventPage = () => {
  const currentUserInfo = useReactiveVar(userInfo);

  const today = getToday();
  const storedSelectedDate = useSelectedDateStore(useShallow(state => state.selectedDate));
  const setSelectedDate = useSelectedDateStore.getState().setSelectedDate;

  const selectedDate = storedSelectedDate >= today ? storedSelectedDate : today;

  const firstDayOfWeek = getFirstDayOfTheWeek(selectedDate);
  const lastDayOfWeek = getLastDayOfTheWeek(selectedDate);

  const pageMode = getPageMode(currentUserInfo.Privileges, currentUserInfo.SystemRole.Id);
  const settings = pageSettings.get(pageMode);

  const breadcrumbPathItems = [
    { name: 'Calendar', url: previousLink },
    { name: 'Add Event', url: '' }
  ];

  const [showErrorMessage, setShowErrorMessage] = useState(false);
  const [manualError, setManualError] = useState(false);

  const [selections, setSelections] = useState({
    eventType: settings.eventType,
    startDate: firstDayOfWeek,
    endDate: lastDayOfWeek,
    orgId: null,
    orgUnitIds: [],
    domains: [],
    atl: null
  });

  const { error: hasReachedEventsLimitError } = useBackgroundQuery(HAS_USER_REACHED_EVENTS_LIMIT, {
    variables: {
      userId: selections.atl?.value,
      year: selections.startDate.getFullYear()
    },
    fetchPolicy: 'no-cache',
    skip: !selections.atl || !selections.startDate,
    onCompleted: data => {
      if (data.hasUserReachedEventsLimit) {
        showReachedEventsLimitToast();
      }
    }
  });

  const navigate = useNavigate();

  const {
    data: orgsAndUnitsData,
    loading: orgsAndUnitsLoading,
    error: orgsAndUnitsError
  } = useQuery(GET_ORGS_WITH_ACTIVE_UNITS, {
    variables: {
      eventType: settings.eventType
    }
  });

  const [addEvent, { error: addEventError }] = useMutation(ADD_EVENT);

  const handleDateRangeChanged = (startDate, endDate) => {
    setSelections(prevState => ({
      ...prevState,
      startDate: startDate,
      endDate: endDate
    }));
  };

  const setFirstAvailableDate = useMemo(
    () => excludedDateIntervals => {
      const firstAvailableDate = getFirstAvailableDate(firstDayOfWeek, excludedDateIntervals);

      const firstDayOfAvailableWeek = getFirstDayOfTheWeek(firstAvailableDate);
      const lastDayOfAvailableWeek = getLastDayOfTheWeek(firstAvailableDate);

      handleDateRangeChanged(firstDayOfAvailableWeek, lastDayOfAvailableWeek);
    },
    [firstDayOfWeek]
  );

  const {
    excludedDateIntervals,
    loading: unavailablePeriodsLoading,
    error: unavailablePeriodsError
  } = useGetUnavailablePeriodsQuery(
    {
      onCompleted: setFirstAvailableDate
    },
    settings.isAutoAssignATL
  );

  const { availableAtlList, availableAtlsLoading, availableAtlsError } = useAvailableAtlList(
    selections.startDate,
    selections.endDate,
    selections.atl,
    null,
    setSelections,
    null,
    settings.isAutoAssignATL
  );

  const handleSaveBtnClick = async () => {
    const eventFormObject = {
      ...selections,
      startDate: formatDate(selections.startDate),
      endDate: formatDate(selections.endDate),
      atl: selections.atl ? selections.atl.value : null
    };

    const validationError = EventInputValidator.validateAddEventData(eventFormObject).error;

    if (validationError) {
      setShowErrorMessage(true);
      return;
    }

    const { data: data } = await addEvent({
      variables: {
        event: CalendarEventMapper.toApolloAddEventInput(eventFormObject)
      }
    });

    if (data.addEvent.Success) {
      setSelectedDate(selections.startDate);
      showSuccessToast('Event added successfully.');
      navigate(previousLink);
      return;
    }

    if (
      data.addEvent.Errors.length == 1 &&
      data.addEvent.Errors.find(
        x => x.Code == VALIDATION_ERROR_CODES.APPRAISER_REACHED_EVENTS_LIMIT
      )
    ) {
      showReachedEventsLimitToast();
      return;
    }

    setManualError(true);
    return;
  };

  const showReachedEventsLimitToast = () => {
    showWarningToast(
      `Annual appraisal assignment limit reached for ${selections.startDate.getFullYear()}.`
    );
  };

  if (
    orgsAndUnitsError ||
    addEventError ||
    availableAtlsError ||
    unavailablePeriodsError ||
    hasReachedEventsLimitError ||
    manualError
  ) {
    return <ApolloError />;
  }

  return (
    <Page title={pageTitle} className='event-page'>
      <Breadcrumb pathItems={breadcrumbPathItems} />
      <StickyHeader>
        <div className='page-header'>
          <PageInstructions title={pageTitle} description={pageDescription} />
          <div className='action-group'>
            <div className='action-button-group'>
              <LinkButton className='secondary-button sm' to={previousLink}>
                Cancel
              </LinkButton>
              <Button className='primary-button sm' onClick={handleSaveBtnClick}>
                Save
              </Button>
            </div>
          </div>
        </div>
        {showErrorMessage && (
          <ErrorMessage className='error-message' message='Please enter Required* fields.' />
        )}
      </StickyHeader>
      <div>
        <OrgOrgUnitSelection
          organizations={orgsAndUnitsData ? orgsAndUnitsData.orgsWithActiveUnits : []}
          selectedOrgId={selections.orgId}
          selectedOrgUnitIds={selections.orgUnitIds}
          setSelections={setSelections}
          loading={orgsAndUnitsLoading}
        />
        <EventDates
          selectedStartDate={selections.startDate}
          selectedEndDate={selections.endDate}
          onDateRangeChanged={handleDateRangeChanged}
          excludedDateIntervals={excludedDateIntervals}
          minDate={settings.getMinDate()}
          label='Event Dates'
          name='eventDates'
          loading={unavailablePeriodsLoading}
        />
        <AtlSelection
          atlList={availableAtlList}
          eventFormData={selections}
          setSelections={setSelections}
          availableAtlsLoading={availableAtlsLoading}
          selectionType={settings.atlSelectionType}
          eventATLExternalId={settings.getSelectedAtlId(selections.atl)}
        />
      </div>
    </Page>
  );
};

const PAGE_MODE = {
  REGULAR_EVENT: 1,
  DC_EVENT_ATL: 2
};

const getPageMode = (privileges, systemRoleId) => {
  if (
    privileges.includes(PrivilegeNames.Calendar_AddEvent_DirectChannel) &&
    systemRoleId === ISACA_MDDAP_APPRAISER
  ) {
    return PAGE_MODE.DC_EVENT_ATL;
  }

  return PAGE_MODE.REGULAR_EVENT;
};

const pageSettings = new Map([
  [
    PAGE_MODE.REGULAR_EVENT,
    {
      atlSelectionType: ATL_SELECTION_TYPE.ATL,
      getMinDate: () => null,
      getSelectedAtlId: _ => null,
      eventType: APPRAISER_EVENT_TYPE.REGULAR
    }
  ],
  [
    PAGE_MODE.DC_EVENT_ATL,
    {
      atlSelectionType: ATL_SELECTION_TYPE.VIEW_ATL,
      getMinDate: () => getToday(),
      getSelectedAtlId: selectedAtl => selectedAtl?.value,
      isAutoAssignATL: true,
      eventType: APPRAISER_EVENT_TYPE.DIRECT_CHANNEL
    }
  ]
]);

const previousLink = '/calendar';
const pageTitle = 'Add Event';
const pageDescription =
  'To add an event, first select the organization and corresponding unit(s) that will be included. ' +
  'Then confirm or edit the start and end dates of the event, which default to the week selected ' +
  'from the calendar page. If known, optionally select any domain(s) applicable for the event, ' +
  'which will filter the available list of ATLs. If known, optionally select the ATL which will ' +
  'be assigned to the event.When ready, select save to add the event, or cancel to discard the event. ' +
  'If an ATL is selected, they will be notified of their assignment via email.';

export default AddEventPage;
